import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, Input, Modal, DatePicker, Typography, Row, Col, Select, Form, Button, Popconfirm,
} from 'antd';
import { PropTypes } from 'prop-types';
import { CalendarOutlined } from '@ant-design/icons';
import InfoView from 'components/common/InfoView';
import 'jspdf-autotable';
import { capitalize } from 'lodash';
import { getProcessors } from '../../redux/actions/Processors';
import {
  downloadTransactions,
  getTransactions,
  createChargeBack,
  updateRefundStatus,
  updateSettlementStatus,
  updateChargebackStatus,
  downloadSummary,
  checkTransactionStatus,
  triggerWebhook,
} from '../../redux/actions/Transactions';
import { createRefund } from '../../redux/actions/Collections';
import PageLimitSwitch from '../../components/PageLimitSwitch';
import { transactionColumns, searchableFields } from './Columns';
import DownloadBtns from '../../components/common/downloadbtns';
import TransactionDetailsForModal from './TransactionDetailsForModal';
import RefundUpdateForm from './TransactionRefundUpdateForm';
import TransactionSettlementForm from './TransactionSettlementUpdateForm';
import TransactionChargebackForm from './TransactionChargebackUpdateForm';
import { TransactionTypes, TransactionStatuses } from '../../constants/System';
import { TRANSACTION_CHARGEBACKS } from '../../constants/resources';
import Can from '../../util/can';
import TransactionCreateRefundForm from './TransactionCreateRefundForm';
import { SHOW_ERROR_MESSAGE } from '../../constants/ActionTypes';
import { dateAndTimeRangeQueriesArray, dateTimeRangeCondition } from '../../util/lib';
import DateRange from '../../containers/DateRange';
import GeneratePRN from '../../containers/GeneratePRN';

const Transactions = ({ merchantId, transactionType, transactionStatus }) => {
  const dispatch = useDispatch();
  const { timezone } = useSelector(({ settings }) => settings);
  const {
    data: allTransactions,
    currentPage,
    pageSize,
    totalRecords,
    loading: allTransactionsLoading,
    downloadLoading,
  } = useSelector(({ transactions }) => transactions.allTransactions);
  const {
    data: processorsData,
    loading: processorsLoading,
  } = useSelector(({ processors }) => processors.allProcessors);
  const { dateRange } = useSelector(({ date }) => date);

  const loading = allTransactionsLoading || processorsLoading;

  const [downloadType, setDownloadType] = useState(null);
  const [visibleTransactionModal, setVisibilityTransactionModal] = useState(false);
  const [visibleUpdateRefundStatusModal, setVisibleUpdateRefundStatusModal] = useState(false);
  const [visibleUpdateSettlmtStatusModal, setVisibleUpdateSettlmtStatusModal] = useState(false);
  const [visibleUpdateChargebackStatusModal, setVisibleUpdateChargebkStatusModal] = useState(false);
  const [visibleCreateRefundModal, setVisibleCreateRefundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [searchValue, setSearchSearchValue] = useState('');
  const [downloadRefunds, setDownloadRefunds] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [transactionFilterParams, setTransactionFilterParams] = useState([]);
  const [transactionsFilter, setTransactionsFilter] = useState('');
  const [selectedTransactionType, setSelectedTransactionType] = useState('All');
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState('All');

  const formEditRefund = useRef();
  const formEditSettlement = useRef();
  const formEditChargeback = useRef();
  const formCreateRefund = useRef();

  const generateTransactionsFilter = (merchantId, transactionStatus, transactionType) => {
    const _transactionFilterParams = [];
    if (merchantId) {
      const transactionMerchantFilter = `{"merchant": "${merchantId}"}`;
      _transactionFilterParams.push(transactionMerchantFilter);
    }

    if (transactionStatus) {
      const transactionStatusFilterParams = [];
      transactionStatus.split(',').forEach((ele) => {
        if (ele.trim().length) {
          transactionStatusFilterParams.push({ transaction_status: ele.trim() });
        }
      });
      const transactionStatusFilter = `{"$or": ${JSON.stringify(transactionStatusFilterParams)}}`;
      _transactionFilterParams.push(transactionStatusFilter);
    }

    if (transactionType && transactionType.length > 3) {
      _transactionFilterParams.push(JSON.stringify({
        transaction_type: {
          $in: transactionType.split(','),
        },
      }));
    }

    let _transactionsFilter = '';
    if (_transactionFilterParams.length > 0) {
      _transactionsFilter = `"$and":[${_transactionFilterParams.join(',')}]`;
    }

    if (transactionType && transactionType.length > 3) {
      setSelectedTransactionType(capitalize(`${transactionType.toLowerCase()}s`));
    } else {
      setSelectedTransactionType('All');
    }

    if (transactionStatus && transactionStatus.length > 3) {
      setSelectedTransactionStatus(capitalize(`${transactionStatus.toLowerCase()}`));
    } else {
      setSelectedTransactionStatus('All');
    }

    setTransactionFilterParams(_transactionFilterParams);
    setTransactionsFilter(_transactionsFilter);

    return _transactionsFilter;
  };

  useEffect(() => {
    // Generate filters and fetch data in one useEffect
    const _transactionsFilter = generateTransactionsFilter(merchantId, transactionStatus, transactionType);
    setSearchParams('');
    setSearchSearchValue('');

    let query = "";
    const sort = `sort={"created_at": -1 }`;
    const paginate = `page=${currentPage}&limit=${pageSize}`;
    const rangeStr = dateRange ? `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}` : null;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    if (transactionFilterParams.length > 0) {
      filters.push(_transactionsFilter);
    }
    if (rangeStr) {
      filters.push(rangeStr);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      query += `filter={${filter}}`;
    } else {
      query += `filter={}`;
    }

    query += `&${sort}&${paginate}`;
    dispatch(getTransactions(query));
    dispatch(getProcessors());
    // eslint-disable-next-line
  }, [dispatch, pageSize, currentPage, dateRange]);

  const handleOnChange = (pagination, _, sort) => {
    if (pagination && sort) {
      const paginate = `page=${pagination.current || currentPage}&limit=${pageSize}`;
      let sortParams = 'sort={"created_at": -1 }';

      if (Object.keys(sort).length && sort.field) {
        sortParams = `sort={"${sort.field}": ${sort.order === 'ascend' ? '1' : '-1'} }`;
      }
      let paginateAndSort = `${paginate}&${sortParams}`;

      const filters = [];
      if (merchantId) {
        filters.push(`"merchant": "${merchantId}"`);
      }
      if (searchParams && searchParams.length > 0) {
        filters.push(`"$or": ${JSON.stringify(searchParams)}`);
      }
      if (dateRange) {
        const rangeStr = `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}`;
        filters.push(rangeStr);
      }
      if (transactionFilterParams.length > 0) {
        filters.push(transactionsFilter);
      }

      if (filters.length > 0) {
        const filter = `"$and":[{${filters.join('},{')}}]`;
        paginateAndSort += `&filter={${filter}}`;
      } else {
        paginateAndSort += `&filter={}`;
      }

      dispatch(getTransactions(paginateAndSort));
    }
  };

  const viewTransactionDetails = (record) => ({
    onClick: () => {
      setSelectedTransaction(record);
      setVisibilityTransactionModal(true);
    },
  });

  const handleSearch = (_searchValue, _, _transactionFilter) => {
    const __searchValue = _searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1');
    setSearchSearchValue(__searchValue);
    let params = searchableFields.map((field) => ({
      [field]: { $regex: __searchValue, $options: 'i' },
    }));

    if (__searchValue.includes('KG')) {
      params = [{ kitegateway_reference: { $regex: __searchValue, $options: 'i' } }];
    } else if (__searchValue.includes('******')) {
      params = [{ account_number: { $regex: __searchValue, $options: 'i' } }];
    } else if (['COLLECTION', 'SETTLEMENT', 'REFUND', 'CHARGEBACK', 'DISBURSEMENT', 'WITHDRAWAL', 'DEPOSIT'].includes(__searchValue.toUpperCase())) {
      params = [{ transaction_type: { $regex: __searchValue.toUpperCase(), $options: 'i' } }];
    } else if (['PENDING', 'CONFIRMING', 'COMPLETED', 'CANCELLED', 'FAILED', 'PROCESSING'].includes(__searchValue.toUpperCase())) {
      params = [{ transaction_status: { $regex: __searchValue.toUpperCase(), $options: 'i' } }];
    } else if (__searchValue?.trim()?.length === 0) {
      params = [];
    }

    setSearchParams(params);
    let search = '';
    const sort = `sort={"created_at": -1 }`;
    const paginate = `page=${currentPage}&limit=${pageSize}`;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    if (_transactionFilter || transactionFilterParams.length > 0) {
      filters.push(_transactionFilter || transactionsFilter);
    }
    if (dateRange) {
      const rangeStr = `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}`;
      filters.push(rangeStr);
    }
    if (params && params.length > 0) {
      filters.push(`"$or": ${JSON.stringify(params)}`);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      search = `filter={${filter}}&${sort}&${paginate}`;
    } else {
      search = `filter={}&${sort}&${paginate}`;
    }

    dispatch(getTransactions(search));
  };

  const handleDownload = (type = null) => {
    if (!dateRange) {
      dispatch({ type: SHOW_ERROR_MESSAGE, payload: 'Select Date range' });
      return;
    }
    const newPageSize = 5000;
    const newCurrentPage = 1;
    const rangeStr = dateRange ? `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}` : '';
    let searchParameters = rangeStr ? [{ ...(JSON.parse(`{${rangeStr}}`)) }] : [];
    let dateRangeParams;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    if (downloadRefunds) {
      filters.push(`"transaction_type": "REFUND"`);
    }
    if (searchParameters.length > 0) {
      filters.push(rangeStr);
    }
    if (searchParams && searchParams.length > 0) {
      filters.push(`"$or": ${JSON.stringify(searchParams)}`);
    }
    if (transactionFilterParams.length > 0) {
      filters.push(transactionsFilter);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      dateRangeParams = `filter={${filter}}&sort={"created_at":-1}&limit=${newPageSize}&page=${newCurrentPage}`;
    } else {
      dateRangeParams = `filter={${rangeStr}}&sort={"created_at":-1}&limit=${newPageSize}&page=${newCurrentPage}`;
    }

    dispatch(downloadTransactions(dateRangeParams, downloadType || type));
  };

  const handleDownloadSummary = (type = null) => {
    if (!dateRange) {
      dispatch({ type: SHOW_ERROR_MESSAGE, payload: 'Select Date range' });
      return;
    }
    const dateRangeParams = `${dateAndTimeRangeQueriesArray(dateRange.startDate, dateRange.endDate, timezone).join('&')}`;
    dispatch(downloadSummary(dateRangeParams, type));
  };

  function openDownloadModal(type) {
    setDownloadRefunds(false);
    setDownloadType(type);
    handleDownload(type);
  }

  const handleModalCancelTransactionModal = () => {
    setVisibilityTransactionModal(false);
    setSelectedTransaction({});
  };

  const handleXclRefunds = () => {
    setDownloadRefunds(true);
    setDownloadType('excel');
  };

  const changeDateRange = () => {
    setDownloadRefunds(false);
  };

  const chargeBack = (record) => {
    dispatch(createChargeBack({ kitegateway_reference: record.kitegateway_reference }));
    setTimeout(() => {
      handleModalCancelTransactionModal();
    }, 2000);
  };

  const handleCheckTransactionStatus = (serviceType, kitegatewayReference) => {
    dispatch(checkTransactionStatus(serviceType, kitegatewayReference));
  };

  const handleTriggerWebhook = (kitegatewayReference) => {
    dispatch(triggerWebhook(kitegatewayReference));
  };

  const handleUpdateTransactionStatus = () => {
    setVisibilityTransactionModal(false);
    if (selectedTransaction.transaction_type === TransactionTypes.REFUND) {
      if (formEditRefund.current) {
        formEditRefund.current.resetFields();
      }
      setVisibleUpdateRefundStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateRefundStatusModal(false);
        if (formEditRefund.current) {
          formEditRefund.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateRefundStatusModal(true);
        }, 1);
      }, 1);
    } else if (selectedTransaction.transaction_type === TransactionTypes.SETTLEMENT) {
      if (formEditSettlement.current) {
        formEditSettlement.current.resetFields();
      }
      setVisibleUpdateSettlmtStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateSettlmtStatusModal(false);
        if (formEditSettlement.current) {
          formEditSettlement.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateSettlmtStatusModal(true);
        }, 1);
      }, 1);
    } else if (selectedTransaction.transaction_type === TransactionTypes.CHARGEBACK) {
      if (formEditChargeback.current) {
        formEditChargeback.current.resetFields();
      }
      setVisibleUpdateChargebkStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateChargebkStatusModal(false);
        if (formEditChargeback.current) {
          formEditChargeback.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateChargebkStatusModal(true);
        }, 1);
      }, 1);
    }
  };

  const handleRefundTransaction = () => {
    setVisibilityTransactionModal(false);
    if (formCreateRefund.current) {
      formCreateRefund.current.resetFields();
    }
    setVisibleCreateRefundModal(true);
    setTimeout(() => {
      setVisibleCreateRefundModal(false);
      if (formCreateRefund.current) {
        formCreateRefund.current.resetFields();
      }
      setTimeout(() => {
        setVisibleCreateRefundModal(true);
      }, 1);
    }, 1);
  };

  const handleModalCancelUpdateStatusModal = () => {
    setVisibleUpdateRefundStatusModal(false);
    setVisibleUpdateSettlmtStatusModal(false);
    setVisibleUpdateChargebkStatusModal(false);
    setSelectedTransaction({});
  };

  const handleModalCancelCreateRefundModal = () => {
    setVisibleCreateRefundModal(false);
    handleModalCancelUpdateStatusModal();
  };

  const handleStatusChange = (value) => {
    setNewStatus(value);
  };

  const handleOnClearTransactionType = () => {
    setSelectedTransactionType('All');
    const _transactionFilter = generateTransactionsFilter(merchantId, transactionStatus, "");
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleOnClearTransactionStatus = () => {
    setSelectedTransactionStatus('All');
    const _transactionFilter = generateTransactionsFilter(merchantId, "", transactionType);
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleTransactionTypeChange = (value) => {
    const newValue = value ?? "All";
    const _transactionFilter = generateTransactionsFilter(
      merchantId,
      transactionStatus,
      ['ALL', 'All'].includes(newValue) ? "" : newValue.toUpperCase()
    );
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleTransactionStatusChange = (value) => {
    const newValue = value ?? "All";
    const _transactionFilter = generateTransactionsFilter(
      merchantId,
      ['ALL', 'All'].includes(newValue) ? "" : newValue.toUpperCase(),
      transactionType
    );
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleModalOkUpdateRefundStatusModal = () => {
    if (formEditRefund.current) {
      formEditRefund.current.validateFields().then((value) => {
        if (!newStatus) return;
        if (newStatus === 'COMPLETED' && !value.processor_reference && !value.refund_id) return;

        dispatch(updateRefundStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkUpdateSettlementStatusModal = () => {
    if (formEditSettlement.current) {
      formEditSettlement.current.validateFields().then((value) => {
        if (!newStatus) return;

        dispatch(updateSettlementStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkUpdateChargebackStatusModal = () => {
    if (formEditChargeback.current) {
      formEditChargeback.current.validateFields().then((value) => {
        if (!newStatus) return;

        dispatch(updateChargebackStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkCreateRefundModal = () => {
    if (formCreateRefund.current) {
      formEditRefund.current.validateFields().then((value) => {
        dispatch(createRefund({
          ...value,
          amount: Number(value.amount)
        }));
        setTimeout(() => {
          handleModalCancelCreateRefundModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const props = {
    loading,
    pagination: false,
    rowKey: '_id',
    columns: transactionColumns(processorsData, timezone),
    dataSource: allTransactions.map((item) => ({
      ...item,
      processor: {
        _id: item.processor,
        name: item.processor?.name || processorsData.find((element) => element._id === item.processor)?.name,
        service_type: item.processor?.service_type || processorsData.find((element) => element._id === item.processor)?.service_type,
      },
    })),
    onChange: handleOnChange,
    onRow: viewTransactionDetails,
  };

  return (
    <div>
      <div className="justify-content-center">
        <Row>
          <Col xl={24} lg={24} md={24} sm={24} xs={24}>
            <DateRange />
          </Col>
        </Row>
        <Row>
          {merchantId && (
            <Col xl={4} lg={4} md={4} sm={4} xs={4}>
              <GeneratePRN merchantId={merchantId} />
            </Col>
          )}
          {!transactionType && (
            <Col xl={3} lg={5} md={6} sm={6} xs={13}>
              <Form name="basic" className="gx-form-row0">
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  value={selectedTransactionType}
                  onClear={handleOnClearTransactionType}
                  placeholder="Select Transaction Type"
                  onChange={handleTransactionTypeChange}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  {Object.values(TransactionTypes).map((type) => (
                    <Select.Option key={type} value={type}>
                      {capitalize(type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form>
            </Col>
          )}
          {!transactionStatus && (
            <Col xl={3} lg={5} md={6} sm={6} xs={13}>
              <Form name="basic" className="gx-form-row0">
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  value={selectedTransactionStatus}
                  onClear={handleOnClearTransactionStatus}
                  placeholder="Select Transaction Status"
                  onChange={handleTransactionStatusChange}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  {Object.values(TransactionStatuses).map((status) => (
                    <Select.Option key={status} value={status}>
                      {capitalize(status)}
                    </Select.Option>
                  ))}
                </Select>
              </Form>
            </Col>
          )}
          <Col xl={merchantId ? 6 : 10} lg={merchantId ? 6 : 10} md={merchantId ? 6 : 12} sm={merchantId ? 6 : 12} xs={merchantId ? 6 : 12}>
            <Input.Search
              placeholder="Search transactions"
              enterButton
              onSearch={handleSearch}
            />
          </Col>
          <Col xl={6} lg={9} md={6} sm={6} xs={13}>
            <DownloadBtns
              handleXclRefunds={[null, TransactionTypes.REFUND].includes(transactionType) ? handleXclRefunds : null}
              handlePdf={() => openDownloadModal('pdf')}
              handleXcl={() => openDownloadModal('excel')}
              handleSummary={() => handleDownloadSummary()}
            />
          </Col>
        </Row>
        <Table className="gx-table-responsive" {...props} />
        <Row className="pagination">
          <Col md={12}>
            <PageLimitSwitch
              resourceType="allTransactions"
              limit={pageSize}
              limits={[10, 100, 200, 500, 1000, 2000, 5000, 10000]}
              totalRecords={totalRecords}
              currentPage={currentPage}
            />
          </Col>
        </Row>
        <InfoView />
      </div>
      <Modal
        visible={visibleTransactionModal}
        okText="Close"
        confirmLoading={loading}
        onCancel={handleModalCancelTransactionModal}
        onOk={handleModalCancelTransactionModal}
        footer={selectedTransaction.kitegateway_reference ? `P/R: ${selectedTransaction.kitegateway_reference}` : null}
      >
        {selectedTransaction?.transaction_type === TransactionTypes.COLLECTION &&
          selectedTransaction?.transaction_status === TransactionStatuses.COMPLETED && (
            <Can I="create" a={TRANSACTION_CHARGEBACKS}>
              <Popconfirm
                title={`Are you sure? ${selectedTransaction.transaction_amount} ${selectedTransaction.transaction_currency?.code} will be debited.`}
                okText="Yes"
                cancelText="No"
                onConfirm={() => chargeBack(selectedTransaction)}
              >
                <Button type="primary" className="ant-btn-sm ant-btn-background-ghost">
                  Add Charge back
                </Button>
              </Popconfirm>
            </Can>
          )}
        <TransactionDetailsForModal
          selectedTransaction={selectedTransaction}
          handleUpdateTransactionStatus={handleUpdateTransactionStatus}
          handleRefundTransaction={handleRefundTransaction}
          handleCheckTransactionStatus={handleCheckTransactionStatus}
          handleTriggerWebhook={handleTriggerWebhook}
        />
      </Modal>
      {selectedTransaction?.kitegateway_reference && (
        <Modal
          visible={visibleUpdateRefundStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateRefundStatusModal}
          okText="Update"
        >
          <RefundUpdateForm
            form={formEditRefund}
            handleStatusChange={handleStatusChange}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleUpdateSettlmtStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateSettlementStatusModal}
          okText="Update"
        >
          <TransactionSettlementForm
            form={formEditSettlement}
            handleStatusChange={handleStatusChange}
            selectedStatus={newStatus}
            selectedTransaction={selectedTransaction}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleUpdateChargebackStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateChargebackStatusModal}
          okText="Update"
        >
          <TransactionChargebackForm
            form={formEditChargeback}
            handleStatusChange={handleStatusChange}
            selectedStatus={newStatus}
            selectedTransaction={selectedTransaction}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleCreateRefundModal}
          confirmLoading={loading}
          onCancel={handleModalCancelCreateRefundModal}
          onOk={handleModalOkCreateRefundModal}
          okText="Refund"
        >
          <TransactionCreateRefundForm
            form={formCreateRefund}
            selectedTransaction={selectedTransaction}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}
    </div>
  );
};

Transactions.propTypes = {
  merchantId: PropTypes.string,
  transactionType: PropTypes.string,
  transactionStatus: PropTypes.string,
};

Transactions.defaultProps = {
  merchantId: null,
  transactionType: null,
  transactionStatus: null,
};

export default Transactions;
